import React, { useState, useEffect, useMemo } from 'react';
import { istDate } from '../lib/ist';
import axios from 'axios';
import { Mail, Phone, MapPin, Calendar, CheckCircle, Clock, Users, TrendingUp, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { API, useAuth } from '../App';
import ScrollSentinel from '../components/ScrollSentinel';

const PAGE_SIZE = 50;

const ContactsList = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [tab, setTab] = useState('customers'); // customers | venues
  const { token } = useAuth();

  const fetchContacts = async (append = false) => {
    if (append) setLoadingMore(true);
    try {
      const params = { limit: PAGE_SIZE };
      if (append && contacts.length) params.before = contacts[contacts.length - 1].created_at;
      const response = await axios.get(`${API}/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const rows = response.data || [];
      setContacts(append ? [...contacts, ...rows] : rows);
      setHasMore(rows.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
    setLoading(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="contacts-list-page">
      <div className="mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl text-charcoal-900">Leads &amp; Customers</h1>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-charcoal-900/10 pb-2 mb-6">
        {[
          { id: 'customers', label: 'Customer Data' },
          { id: 'venues', label: 'Venue Leads' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`contacts-tab-${t.id}`}
            className={`px-4 py-2 rounded-t-lg text-sm ${
              tab === t.id ? 'bg-charcoal-900 text-cream-50' : 'text-charcoal-800/70 hover:bg-cream-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'customers' && <CustomersSection />}

      {tab === 'venues' && (<>
      <p className="text-charcoal-800/60 mb-4 text-sm">{contacts.length}{hasMore ? '+' : ''} venue inquiries received</p>
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card-dashboard animate-pulse">
              <div className="h-6 w-48 bg-gray-200 rounded mb-3"></div>
              <div className="h-4 w-32 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16 card-dashboard" data-testid="leads-empty-state">
          <Mail className="w-16 h-16 mx-auto text-charcoal-800/20 mb-4" />
          <h3 className="font-serif text-xl text-charcoal-900 mb-2">No leads yet</h3>
          <p className="text-charcoal-800/60">When venues inquire through the website, they'll appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => {
            const footfall = contact.average_footfall_per_month;
            const isHighValue = typeof footfall === 'number' && footfall >= 5000;
            return (
              <div key={contact.id} className="card-dashboard" data-testid={`contact-${contact.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                      <h3 className="font-serif text-lg sm:text-xl text-charcoal-900 truncate">{contact.venue_name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        contact.status === 'new'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {contact.status === 'new' ? (
                          <><Clock className="w-3 h-3 inline mr-1" />New</>
                        ) : (
                          <><CheckCircle className="w-3 h-3 inline mr-1" />Contacted</>
                        )}
                      </span>
                      {isHighValue && (
                        <span className="px-2 py-1 rounded-full text-xs bg-gold-500/10 text-amber-700 border border-amber-300 flex items-center gap-1" data-testid="high-value-badge">
                          <TrendingUp className="w-3 h-3" />
                          High value
                        </span>
                      )}
                    </div>
                    <p className="text-charcoal-800/80 font-medium text-sm sm:text-base">{contact.contact_person}</p>
                  </div>
                  <div className="text-left sm:text-right text-sm text-charcoal-800/40 flex-shrink-0">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    {istDate(contact.created_at)}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-charcoal-900/5">
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-2 text-sm text-charcoal-800/70 hover:text-charcoal-900 transition-colors min-w-0"
                  >
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{contact.email}</span>
                  </a>
                  <a
                    href={`tel:${contact.phone}`}
                    className="flex items-center gap-2 text-sm text-charcoal-800/70 hover:text-charcoal-900 transition-colors"
                  >
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    {contact.phone}
                  </a>
                  <div className="flex items-center gap-2 text-sm text-charcoal-800/70">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">
                      {contact.city || '—'}{contact.location ? `, ${contact.location}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-charcoal-800/70">
                    <Users className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {typeof footfall === 'number'
                        ? `${footfall.toLocaleString()} guests / month`
                        : 'Footfall not provided'}
                    </span>
                  </div>
                </div>

                {contact.notes && (
                  <div className="mt-4 p-3 bg-cream-50 rounded-lg">
                    <p className="text-sm text-charcoal-800/70">
                      <span className="font-medium text-charcoal-900">Notes:</span> {contact.notes}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
          <ScrollSentinel
            onVisible={() => fetchContacts(true)}
            disabled={loading || loadingMore || !hasMore}
            testid="contacts-scroll-sentinel"
          />
          {loadingMore && <div className="text-center py-3 text-xs text-charcoal-800/40">Loading more leads…</div>}
        </div>
      )}
      </>)}
    </div>
  );
};

const CustomersSection = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });

  useEffect(() => {
    axios.get(`${API}/customer-contacts?limit=500`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setRows(r.data.contacts || []); setTotal(r.data.total || 0); })
      .catch(() => {});
  }, [token]);

  const visibleRows = useMemo(() => {
    let out = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q) ||
        (c.kiosk_name || '').toLowerCase().includes(q)
      );
    }
    const { key, dir } = sort;
    return [...out].sort((a, b) => {
      const av = (a[key] ?? '').toString().toLowerCase();
      const bv = (b[key] ?? '').toString().toLowerCase();
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [rows, search, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const Th = ({ colKey, children }) => (
    <th
      className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase cursor-pointer select-none hover:text-charcoal-900"
      onClick={() => toggleSort(colKey)}
      data-testid={`customers-sort-${colKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sort.key === colKey && (sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/customer-contacts/export`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'piczoo-customers.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (_e) { /* noop */ }
  };

  return (
    <div data-testid="customers-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="text-charcoal-800/60 text-sm">{total} customers opted in from the photo download page{search && ` · ${visibleRows.length} matching`}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-800/30" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, booth…"
              className="pl-9 pr-3 py-2 rounded-lg border border-cream-200 bg-white text-sm w-56"
              data-testid="customers-search-input"
            />
          </div>
          {rows.length > 0 && (
            <button onClick={exportCsv} className="text-sm bg-charcoal-900 text-white px-4 py-2 rounded-lg hover:bg-charcoal-800" data-testid="customers-export-btn">
              Export CSV
            </button>
          )}
        </div>
      </div>
      {visibleRows.length === 0 ? (
        <p className="text-sm text-charcoal-800/40 bg-white rounded-xl border border-cream-200 p-6 text-center">
          {search ? 'No customers match your search.' : 'No customer signups yet — they\u2019ll appear here when people opt in on the photo download page.'}
        </p>
      ) : (
        <div className="bg-white rounded-xl border border-cream-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cream-200 text-left">
                <Th colKey="created_at">Date</Th>
                <Th colKey="kiosk_name">Booth</Th>
                <Th colKey="name">Name</Th>
                <Th colKey="email">Email</Th>
                <Th colKey="phone">WhatsApp</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-100">
              {visibleRows.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} className="hover:bg-cream-50">
                  <td className="px-4 py-2.5 text-charcoal-800/60 whitespace-nowrap">{istDate(c.created_at)}</td>
                  <td className="px-4 py-2.5">{c.kiosk_name || '—'}</td>
                  <td className="px-4 py-2.5">{c.name || '—'}</td>
                  <td className="px-4 py-2.5">{c.email ? <a className="hover:underline" href={`mailto:${c.email}`}>{c.email}</a> : '—'}</td>
                  <td className="px-4 py-2.5">{c.phone ? <a className="hover:underline" href={`https://wa.me/${c.phone}`} target="_blank" rel="noreferrer">{c.phone}</a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContactsList;
